import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import { useCatalog, useCategories } from "../hooks/useCatalog";
import { productSubcategories } from "../lib/product";
import { ChevronDown, ChevronUp, Loader2, Search } from "lucide-react";
import { PRODUCTS as P_TID } from "../constants/testIds";

const VISIBLE_INITIAL = 18;

const Products = () => {
    const [showAll, setShowAll] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [subcategoryFilter, setSubcategoryFilter] = useState(null);
    const [search, setSearch] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { data: catalogData, isLoading: catalogLoading, error: catalogError } = useCatalog();
    const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
    const gridRef = useRef(null);
    const didMount = useRef(false);

    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }
        if (subcategoryFilter && gridRef.current) {
            const y = gridRef.current.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    }, [subcategoryFilter]);

    const products = useMemo(() => catalogData?.items || [], [catalogData]);
    const categories = useMemo(() => categoriesData || [], [categoriesData]);

    const selectedCategory = useMemo(
        () => categories.find((c) => c.id === categoryFilter),
        [categories, categoryFilter]
    );

    const filtered = useMemo(() => {
        let result = products;
        const q = search.trim().toLowerCase();

        if (q) {
            result = result.filter((p) => {
                const nameMatch = p.name?.toLowerCase().includes(q);
                const specMatch = (p.specs || []).some((s) =>
                    s.toLowerCase().includes(q),
                );
                const categoryMatch = p.category?.toLowerCase().includes(q);
                const subcategoryMatch = productSubcategories(p).some((s) =>
                    s.toLowerCase().includes(q),
                );
                return nameMatch || specMatch || categoryMatch || subcategoryMatch;
            });
        }

        if (selectedCategory) {
            result = result.filter((p) => p.category === selectedCategory.label);
            if (subcategoryFilter) {
                result = result.filter((p) =>
                    productSubcategories(p).includes(subcategoryFilter),
                );
            }
        }

        // Top ventes en tête (tri stable) : garantit que le badge "Top vente"
        // est visible dans le prérendu (VISIBLE_INITIAL) comme côté client.
        result = [...result].sort(
            (a, b) => (b.top_selling ? 1 : 0) - (a.top_selling ? 1 : 0)
        );

        return result;
    }, [products, selectedCategory, subcategoryFilter, search]);

    const visible = showAll ? filtered : filtered.slice(0, VISIBLE_INITIAL);
    const hasMore = filtered.length > VISIBLE_INITIAL;

    const isLoading = catalogLoading || categoriesLoading;

    const handleCategoryClick = (catId) => {
        if (categoryFilter === catId) {
            setCategoryFilter("all");
            setSubcategoryFilter(null);
        } else {
            setCategoryFilter(catId);
            setSubcategoryFilter(null);
        }
        setShowAll(false);
    };

    const handleSubcategoryClick = (label) => {
        setSubcategoryFilter((prev) => (prev === label ? null : label));
        setShowAll(false);
    };

    // Deep-link from the category grid: ?category=<id> preselects the filter
    useEffect(() => {
        if (categories.length === 0) return;
        const param = searchParams.get("category");
        const valid = categories.some((c) => c.id === param);
        const next = valid ? param : "all";
        setCategoryFilter((prev) => (prev === next ? prev : next));
        if (!valid) setSubcategoryFilter(null);
    }, [searchParams, categories]);

    // Keep the URL in sync when the filter chips change (no extra history entries)
    useEffect(() => {
        if (categories.length === 0) return;
        const current = searchParams.get("category") || "all";
        if (current !== categoryFilter) {
            setSearchParams(
                categoryFilter === "all" ? {} : { category: categoryFilter },
                { replace: true },
            );
        }
    }, [categoryFilter, categories, searchParams, setSearchParams]);

    // Scroll to the catalog when arriving with a category preselected
    useEffect(() => {
        if (!searchParams.get("category")) return;
        const t = setTimeout(() => {
            document
                .getElementById("produits")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
        return () => clearTimeout(t);
    }, [searchParams]);

    // Arrivée via la loupe de la navbar : scroll + focus sur le champ de
    // recherche, puis purge du state de navigation (pas de re-déclenchement
    // au refresh ou au retour arrière).
    useEffect(() => {
        if (!location.state?.focusSearch) return;
        const t = setTimeout(() => {
            const el = document.getElementById("catalog-search");
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.focus({ preventScroll: true });
            }
            navigate(location.pathname + location.search, {
                replace: true,
                state: null,
            });
        }, 150);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);


    return (
        <section
            id="produits"
            data-testid={P_TID.section}
            className="relative bg-[#FBFBFD] pt-8 md:pt-10 pb-24 md:pb-32"
        >
            <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-10">
                {/* Search + category filter */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex items-center">
                            <Search className="absolute left-3.5 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                            <input
                                id="catalog-search"
                                type="text"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setShowAll(false);
                                }}
                                placeholder="coque iphone 17pro max"
                                className="inline-flex items-center font-medium text-[12.5px] py-2 pl-10 pr-4 min-w-[220px] md:min-w-[280px] rounded-full border border-black/[0.12] bg-transparent text-neutral-900 placeholder:text-neutral-500 cursor-text focus:outline-none focus:ring-2 focus:ring-[#d4af37]/20 focus:border-[#d4af37]/40"
                            />
                        </div>
                        <button
                            type="button"
                            data-testid="filter-all"
                            onClick={() => {
                                setCategoryFilter("all");
                                setSubcategoryFilter(null);
                                setShowAll(false);
                            }}
                            className={`dp-btn text-[12.5px] py-2 px-4 ${
                                categoryFilter === "all" ? "dp-btn-primary" : "dp-btn-ghost"
                            }`}
                        >
                            Tous • {products.length}
                        </button>
                        {categories.map((c) => {
                            const count = products.filter((p) => p.category === c.label).length;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    data-testid={`filter-${c.id}`}
                                    onClick={() => handleCategoryClick(c.id)}
                                    className={`dp-btn text-[12.5px] py-2 px-4 ${
                                        categoryFilter === c.id ? "dp-btn-primary" : "dp-btn-ghost"
                                    }`}
                                >
                                    {c.label} • {count}
                                </button>
                            );
                        })}
                    </div>

                    {/* Subcategory filter bar */}
                    <AnimatePresence>
                        {selectedCategory && (selectedCategory.subcategories || []).length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.2 }}
                                className="flex flex-wrap items-center gap-2 pl-0 md:pl-1"
                            >
                                <span className="text-[12px] text-neutral-500 mr-1">
                                    Sous-catégories :
                                </span>
                                {(selectedCategory.subcategories || [])
                                    .filter((s) => s.active)
                                    .sort((a, b) => a.sort_order - b.sort_order)
                                    .map((s) => {
                                        const count = products.filter(
                                            (p) =>
                                                p.category === selectedCategory.label &&
                                                productSubcategories(p).includes(s.label),
                                        ).length;
                                        return (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onClick={() => handleSubcategoryClick(s.label)}
                                                className={`dp-btn text-[11.5px] py-1.5 px-3 rounded-full ${
                                                    subcategoryFilter === s.label
                                                        ? "bg-[#8b5cf6] text-white border-[#8b5cf6]"
                                                        : "dp-btn-ghost"
                                                }`}
                                            >
                                                {s.label} • {count}
                                            </button>
                                        );
                                    })}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Loading state */}
                {isLoading && (
                    <div className="mt-16 flex flex-col items-center justify-center gap-4 text-neutral-500">
                        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
                        <span className="text-sm font-medium">Chargement du catalogue…</span>
                    </div>
                )}

                {/* Error state */}
                {catalogError && !isLoading && (
                    <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
                        <p className="font-medium">Impossible de charger le catalogue.</p>
                        <p className="text-sm mt-1">{catalogError.message}</p>
                    </div>
                )}

                {/* Grid */}
                {!isLoading && (
                    <div ref={gridRef} className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-10 sm:gap-y-12 md:gap-y-14">
                        <AnimatePresence mode="popLayout">
                            {visible.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && visible.length === 0 && (
                    <div className="mt-16 text-center text-neutral-500">
                        Aucun produit ne correspond à {search.trim() ? "cette recherche" : "cette catégorie"}.
                    </div>
                )}

                {/* Voir tout */}
                {hasMore && !isLoading && (
                    <div className="mt-12 flex flex-col items-center gap-3">
                        <button
                            type="button"
                            data-testid="products-see-all"
                            onClick={() => setShowAll((v) => !v)}
                            className="dp-btn dp-btn-ghost"
                        >
                            {showAll ? (
                                <>
                                    <ChevronUp className="w-4 h-4" />
                                    Réduire
                                </>
                            ) : (
                                <>
                                    Voir tout ({filtered.length})
                                    <ChevronDown className="w-4 h-4" />
                                </>
                            )}
                        </button>
                        <div className="text-[12px] font-mono-tech tracking-[0.2em] uppercase text-neutral-400">
                            {visible.length} / {filtered.length} produits affichés
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Products;
